import { View, Text, Pressable, ActivityIndicator } from 'react-native';
import { keeperDuelViewStyles as styles } from './KeeperDuelView.styles';
import {
  KEEPER_DUEL_ACCENT,
  KEEPER_DUEL_TEST_IDS,
  type KeeperDuelTier,
} from '../../../src/quiz/constants/keeperDuel.constants';

interface KeeperDuelOverlaysProps {
  status: 'loading' | 'ready' | 'error';
  errorMessage: string | null;
  runOver: boolean;
  finalStreak: number;
  tier: KeeperDuelTier;
  coinsAwarded: number;
  coinsLeftToday: number;
  capReached: boolean;
  bestStreak: number;
  newBest: boolean;
  toast: string | null;
  onRetry: () => void;
  onPlayAgain: () => void;
  onMoreGames: () => void;
}

/** RN-native overlays over the game canvas: loading, error, and the end-of-run summary. */
export function KeeperDuelOverlays({
  status,
  errorMessage,
  runOver,
  finalStreak,
  tier,
  coinsAwarded,
  coinsLeftToday,
  capReached,
  bestStreak,
  newBest,
  toast,
  onRetry,
  onPlayAgain,
  onMoreGames,
}: KeeperDuelOverlaysProps) {
  return (
    <>
      {status === 'loading' && (
        <View style={styles.overlay} testID={KEEPER_DUEL_TEST_IDS.loading}>
          <ActivityIndicator size="large" color={KEEPER_DUEL_ACCENT} />
          <Text style={styles.message}>Warming up the keeper…</Text>
        </View>
      )}

      {status === 'error' && (
        <View style={styles.overlay} testID={KEEPER_DUEL_TEST_IDS.error}>
          <Text style={styles.message}>{errorMessage ?? 'Something went wrong'}</Text>
          <Pressable
            style={({ pressed }) => [styles.retryButton, pressed && styles.pressedDim]}
            onPress={onRetry}
            testID={KEEPER_DUEL_TEST_IDS.retry}
          >
            <Text style={styles.retryText}>Try again</Text>
          </Pressable>
        </View>
      )}

      {status === 'ready' && runOver && (
        <View style={styles.overlay} testID={KEEPER_DUEL_TEST_IDS.summary}>
          <View style={styles.summaryCard}>
            <Text style={styles.tierEmoji}>{tier.emoji}</Text>
            <Text style={styles.tierName}>{tier.name}</Text>
            <Text style={styles.tierScore}>
              {finalStreak === 1 ? '1 goal before the save' : `${finalStreak} goals before the save`}
            </Text>
            {coinsAwarded > 0 && <Text style={styles.coinsLine}>+{coinsAwarded} coins</Text>}
            {newBest && <Text style={styles.bestLine}>New best streak! 🎉</Text>}
            {!newBest && bestStreak > 0 && <Text style={styles.bestLine}>Best: {bestStreak}</Text>}
            {capReached && (
              <Text style={styles.capNote} testID={KEEPER_DUEL_TEST_IDS.capBanner}>
                Coins done for today — keep playing for the streak
              </Text>
            )}
            {!capReached && coinsAwarded === 0 && finalStreak === 0 && (
              <Text style={styles.capNote}>He read you like a book. Go again.</Text>
            )}
            {!capReached && coinsLeftToday > 0 && coinsAwarded > 0 && (
              <Text style={styles.capNote}>{coinsLeftToday} coins still up for grabs today</Text>
            )}
            <View style={styles.buttons}>
              <Pressable
                style={({ pressed }) => [styles.ghostButton, pressed && styles.pressedDim]}
                onPress={onMoreGames}
                testID={KEEPER_DUEL_TEST_IDS.moreGames}
              >
                <Text style={styles.ghostText}>More games</Text>
              </Pressable>
              <Pressable
                style={({ pressed }) => [styles.retryButton, pressed && styles.pressedDim]}
                onPress={onPlayAgain}
                testID={KEEPER_DUEL_TEST_IDS.playAgain}
              >
                <Text style={styles.retryText}>Go again</Text>
              </Pressable>
            </View>
          </View>
        </View>
      )}

      {toast != null && (
        <View style={styles.toast} pointerEvents="none">
          <Text style={styles.toastText}>{toast}</Text>
        </View>
      )}
    </>
  );
}
